import {SelectionModel} from '@angular/cdk/collections';
import {FlatTreeControl} from '@angular/cdk/tree';
import {
  Component,
  ElementRef,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
} from '@angular/core';
import {MatTreeFlatDataSource, MatTreeFlattener} from '@angular/material/tree';
import {DialogData, DialogValidationComponent} from '@Common/widgets/general/dialog-validation';
import {MatDialog} from '@angular/material/dialog';
import {SubSink} from '@Common/core/utils/subsink';
import {IFlatNode, INode, IFlatNodeWParent, INodeMove} from "@Common/widgets/general/tree-extended/node/node.model";
import {ChecklistDatabase} from "@Common/widgets/general/tree-extended/node/node-database.service";


@Component({
  selector: 'app-tree',
  templateUrl: './tree.component.html',
  styleUrls: ['./tree.component.scss'],
  providers: [ChecklistDatabase]
})
export class TreeComponent<DataNode> implements OnInit, OnDestroy {
  /**
   * mode édition : le drag & drop, l'édit
   * mode création et édition: création possible
   * mode suppression et edition: suppression possible
   * sinon uniquement l'arbre en consultation
   * Dès qu'une modification est apportée, il vaut mieux que le parent recharge les données
   * pour avoir l'ordre tel qu'il est prévu en base ou afficher le node créé / supprimé / modifié
   */
  @Input() editMode = false;
  @Input() createMode = false;
  @Input() deleteMode = false;
  /**
   * C'est le role du composant appelant de définir l'ordre et la liste des noeuds à afficher
   */
  @Input() parents: INode<DataNode>[];
  @Input() currentNodeId: number;
  @Input() nodeNameFct: (node: IFlatNode<DataNode>) => string;
  @Input() titleAdd = 'Ajouter un node';

  // editMode === true
  @Output() onMoveNode = new EventEmitter();
  @Output() onEditNode = new EventEmitter();
  // createMode === true & editMode === true
  @Output() onAddNode = new EventEmitter();
  // deleteMode === true & editMode === true
  @Output() onDeleteNode = new EventEmitter();
  /**
   Callback pour savoir si on peut effacer le node lorsque le mode deleteNode est actif
   Paramètre optionnel, par défaut le delete est autorisé
  */
  @Input() canDeleteFct: (node: IFlatNode<DataNode>) => boolean = (node) => true;
  // editMode === false
  @Output() onClick = new EventEmitter();

  /** Map from flat node to nested node. This helps us finding the nested node to be modified */
  flatNodeMap = new Map<IFlatNode<DataNode>, INode<DataNode>>();

  /** Map from nested node to flattened node. This helps us to keep the same object for selection */
  nestedNodeMap = new Map<INode<DataNode>, IFlatNode<DataNode>>();

  treeControl: FlatTreeControl<IFlatNode<DataNode>>;

  treeFlattener: MatTreeFlattener<INode<DataNode>, IFlatNode<DataNode>>;

  dataSource: MatTreeFlatDataSource<INode<DataNode>, IFlatNode<DataNode>>;

  /** The selection for checklist */
  checklistSelection = new SelectionModel<IFlatNode<DataNode>>(true /* multiple */);

  /* Drag and drop */
  dragNode: IFlatNode<DataNode>;
  dragNodeExpandOverWaitTimeMs = 300;
  dragNodeExpandOverNode: IFlatNode<DataNode>;
  dragNodeExpandOverTime: number;
  dragNodeExpandOverArea: string;
  @ViewChild('emptyItem', {static: true}) emptyItem: ElementRef;
  selectedNode = null;
  subSink = new SubSink();

  constructor(private database: ChecklistDatabase<DataNode>, public dialog: MatDialog) {
  }

  ngOnInit() {
    this.database.edit = this.editMode;
    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel, this.isExpandable, this.getChildren);
    this.treeControl = new FlatTreeControl<IFlatNode<DataNode>>(this.getLevel, this.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
    this._initDataSource();
    this.subSink.sink = this.database.dataChange.subscribe(data => {
      this.dataSource.data = [];
      this.dataSource.data = data;
      const currentNode = this.treeControl.dataNodes.find (
        (element: IFlatNode<DataNode>) => element.item.id === this.currentNodeId
      );
      if (currentNode) {
        this.treeControl.expand(currentNode);
        this._expandParentNode(currentNode);
      }
    });
  }

  ngOnChanges() {
    this._initDataSource();
  }

  private _initDataSource() {
    if (this.parents && this.dataSource) {
      this.database.initialize(this.parents);
    }
  }

  getLevel = (node: IFlatNode<DataNode>) => node.level;

  isExpandable = (node: IFlatNode<DataNode>) => node.expandable;

  getChildren = (node: INode<DataNode>): INode<DataNode>[] => node.children;

  hasChild = (_: number, _nodeData: IFlatNode<DataNode>) => _nodeData.expandable;

  /**
   * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
   */
  transformer = (node: INode<DataNode>, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.item === node.item
      ? existingNode
      : new IFlatNode<DataNode>();
    flatNode.item = node.item;
    flatNode.level = level;
    flatNode.expandable = (node.children && node.children.length > 0);
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  }

  onClickLevel(event, node) {
    this.selectedNode = node;
    this.onClick.emit(node);
  }

  itemSelectionToggle(node: IFlatNode<DataNode>): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);
    this.checklistSelection.isSelected(node)
      ? this.checklistSelection.select(...descendants)
      : this.checklistSelection.deselect(...descendants);
  }

  handleDragStart(event, node: IFlatNode<DataNode>) {
    // Required by Firefox (https://stackoverflow.com/questions/19055264/why-doesnt-html5-drag-and-drop-work-in-firefox)
    event.dataTransfer.setData('foo', 'bar');
    event.dataTransfer.setDragImage(this.emptyItem.nativeElement, 0, 0);
    this.dragNode = node;
    this.treeControl.collapse(node);
  }

  handleDragOver(event, node: IFlatNode<DataNode>) {
    event.preventDefault();

    // Handle node expand
    if (node === this.dragNodeExpandOverNode) {
      if (this.dragNode !== node && !this.treeControl.isExpanded(node)) {
        if ((new Date().getTime() - this.dragNodeExpandOverTime) > this.dragNodeExpandOverWaitTimeMs) {
          this.treeControl.expand(node);
        }
      }
    } else {
      this.dragNodeExpandOverNode = node;
      this.dragNodeExpandOverTime = new Date().getTime();
    }

    // Handle drag area
    const percentageX = event.offsetX / event.target.clientWidth;
    const percentageY = event.offsetY / event.target.clientHeight;
    if (percentageY < 0.25) {
      this.dragNodeExpandOverArea = 'above';
    } else if (percentageY > 0.75) {
      this.dragNodeExpandOverArea = 'below';
    } else {
      this.dragNodeExpandOverArea = 'center';
    }
  }

  handleDrop(event, node: IFlatNode<DataNode>) {
    event.preventDefault();
    if (node !== this.dragNode) {
      // let newItem: INode<DataNode>;
      let pos: string = '';
      if (this.dragNodeExpandOverArea === 'above') {
        pos = 'left';
      } else if (this.dragNodeExpandOverArea === 'below') {
        pos = 'right'
      } else {
        pos = 'last-child'
      }
      // En base l'ordre peut être sur un attribut et ne tiendra pas compte de l'endroit où le node est drop
      // un reload par le parent de '@Input() parent' permet d'afficher l'ordre tel qu'il est en base
      this.onMoveNode.emit(<INodeMove>{dragNodeId: this.dragNode.item.id, targetNodeId: node.item.id, pos: pos});
    }

    this.dragNode = null;
    this.dragNodeExpandOverNode = null;
    this.dragNodeExpandOverTime = 0;
  }

  handleDragEnd(event) {
    this.dragNode = null;
    this.dragNodeExpandOverNode = null;
    this.dragNodeExpandOverTime = 0;
  }

  editNode(flatNode: IFlatNode<DataNode>) {
    const node: INode<DataNode> = this.flatNodeMap.get(flatNode);
    const parent: INode<DataNode> = this.database.getParentFromNodes(node);
    this.onEditNode.emit(<IFlatNodeWParent<DataNode>>{
      node: this.nestedNodeMap.get(node),
      parent: this.nestedNodeMap.get(parent) || null
    });
  }

  addNode() {
    // A la création, le service ChecklistDatabase n'est pas utilsé, donc la mémoire n'est pas mise à jour
    // un reload par le parent de '@Input() parent' permet d'afficher le nouveau node
    this.onAddNode.emit();
  }

  deleteNode(flatNode: IFlatNode<DataNode>) {
    const node: INode<DataNode> = this.flatNodeMap.get(flatNode);
    const parentNode: INode<DataNode> = this.database.getParentFromNodes(node);
    const parent: IFlatNode<DataNode> = this.nestedNodeMap.get(parentNode);

    const data = new DialogData();
    data.title = `Suppression du noeud`;
    data.message = `Voulez-vous vraiment supprimer "${this.nodeNameFct(flatNode)}" ?`;
    const modalConfirm = this.dialog.open(DialogValidationComponent, {data: data, disableClose: true});
    modalConfirm.updatePosition({top: '40px'});
    modalConfirm.afterClosed().subscribe(result => {
      if (result) {
        this.onDeleteNode.emit(<IFlatNodeWParent<DataNode>>{node: flatNode, parent: parent});
      }
    });
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  private _expandParentNode(node: IFlatNode<DataNode>) {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return;
    }

    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (this.getLevel(currentNode) < currentLevel) {
        this.treeControl.expand(currentNode);
        return this._expandParentNode(currentNode);
      }
    }
  }
}
